import React, { useMemo, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	IconButton,
	Link,
	Popover,
	Tooltip,
	Typography,
} from '@mui/material';
import {
	Check,
	Comment,
	ContentCopy,
	HorizontalRule,
	Link as LinkIcon,
	OpenInNew,
} from '@mui/icons-material';
import { DateTime } from 'luxon';
import { ETableStructureType, TTableStructureButton } from 'ui/EasTable/types';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { UserAvatar } from 'ui/UserAvatar';
import { TUser } from 'redux/reducers/user/types';
import { TypeOfDialog } from 'components/HMRC/types';
import { useGetCountryById, useGetCountryByName } from 'utils/getCountry';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
	getEnumsAnnualFeeTypeId,
	getEnumsInvoiceFeeTypeId,
	getEnumsFeeClass,
	getFeeClasses,
	getMonthlyFees,
	getEnumsIossSaleOverThresholdOption,
	getEnumsPaymentMode,
	getEnumsCustomsDataStatus,
	getEnumsWeightUnit,
	getEnumsSupportedCurrency,
	getEnumsCurrencyCode,
	getEnumsReportingFeeActual,
	getEnumsManualFeeClass,
	getEnumsMonthlyFees,
	getEnumsIossDutyMode,
} from 'redux/reducers/emManagement/selectors';
import moment from 'moment-timezone';

import {
	EEmEnums,
	FEES_CLASSES,
	REPORTING_FEE_TYPE_ID,
} from 'redux/reducers/emManagement/constants';
import { COLORS } from 'services/constants/colors';
import { ICON_BUTTONS, EIconButtons } from './constants';
import { getEmailsTypes } from '../../../../redux/reducers/emails/selectors';
import { getSmsTypes } from '../../../../redux/reducers/sms/selectors';
import { EasTooltip } from '../../../EasTooltip';

const STATUS_ARRAY_VISIBLE_LIMIT = 4;

interface EasTableValueProps {
	type: ETableStructureType;
	value: any;
	color?: string;
	item?: any;
	tableStructureItem?: any;
	selectedRow?: number | null;
	index?: number;
	handleActionStopPropagation?: (event: any, id: number, dialogType: TypeOfDialog) => void;
	size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';
	bold?: boolean;
	tooltip?: React.ReactNode;
	chip?: boolean;
}

export const EasTableValue: React.FC<EasTableValueProps> = ({
	type,
	value,
	item,
	tableStructureItem,
	selectedRow,
	index,
	handleActionStopPropagation,
	size = 's',
	bold = false,
	tooltip,
	chip = false,
	color = COLORS.font_main,
}) => {
	const registeredCountry = useGetCountryById(value);
	const getCountryByname = useGetCountryByName(value as string);
	// console.log('EasTableValue item', item);
	// console.log('registeredCountry', registeredCountry);
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const [statusArrayAnchor, setStatusArrayAnchor] = useState<HTMLElement | null>(null);

	const enumsAnnual = useSelector(getEnumsAnnualFeeTypeId);
	const enumsInvoice = useSelector(getEnumsInvoiceFeeTypeId);
	const enumsFeeClass = useSelector(getEnumsFeeClass);
	const enumsFeeClasses = useSelector(getFeeClasses);
	const enumsReportingFeeType = useSelector(getMonthlyFees);
	// const enumsTimezoneList = useSelector(getEnumsTimezoneList);
	const enumsWeightUnit = useSelector(getEnumsWeightUnit);
	const enumsCustomsDataStatus = useSelector(getEnumsCustomsDataStatus);
	const enumsPaymentMode = useSelector(getEnumsPaymentMode);
	const enumsIossSaleOverThresholdOption = useSelector(getEnumsIossSaleOverThresholdOption);
	const enumsIossDutyMode = useSelector(getEnumsIossDutyMode);
	const enumsSupportedCurrency = useSelector(getEnumsSupportedCurrency);
	const enumsCurrencyCode = useSelector(getEnumsCurrencyCode);
	const enumsReportingFeeActual = useSelector(getEnumsReportingFeeActual);
	const enumsManualFeeClass = useSelector(getEnumsManualFeeClass);
	const enumsMonthlyFees = useSelector(getEnumsMonthlyFees);
	const enumsEmailTypes = useSelector(getEmailsTypes);
	const enumsSmsTypes = useSelector(getSmsTypes);
	const enumsTimezoneList = moment.tz.names();
	const resolveEnumDisplayName = (
		enumName?: EEmEnums | typeof FEES_CLASSES | typeof REPORTING_FEE_TYPE_ID,
		rawValue?: any,
	): string | null => {
		if (!enumName) return null;
		const valueStr = String(rawValue ?? '');
		let source: any = null;
		if (enumName === EEmEnums.ANNUAL_FEE_TYPE_ID) {
			source = enumsAnnual;
		} else if (enumName === EEmEnums.INVOICE_FEE_TYPE_ID) {
			source = enumsInvoice;
		} else if (enumName === EEmEnums.FEES_CLASS) {
			source = enumsFeeClass;
		} else if (enumName === FEES_CLASSES) {
			source = enumsFeeClasses?.map((classItem) => ({
				value: String(classItem.id),
				displayName: classItem.description,
			}));
		} else if (enumName === REPORTING_FEE_TYPE_ID) {
			source = enumsReportingFeeType?.map((feeItem) => ({
				value: String(feeItem.id),
				displayName: feeItem.description,
			}));
		} else if (enumName === EEmEnums.TIMEZONE_LIST) {
			source = enumsTimezoneList?.map((timezoneItem: string) => ({
				value: String(timezoneItem),
				displayName: `${timezoneItem} ${moment.tz(timezoneItem).format('Z')}`,
			}));
		} else if (enumName === EEmEnums.WEIGHT_UNIT) {
			source = enumsWeightUnit?.map((weightUnitItem) => ({
				value: String(weightUnitItem.value),
				displayName: weightUnitItem.displayName,
			}));
		} else if (enumName === EEmEnums.CUSTOMS_DATA_STATUS) {
			source = enumsCustomsDataStatus?.map((customsDataStatusItem) => ({
				value: String(customsDataStatusItem.value),
				displayName: customsDataStatusItem.displayName,
			}));
		} else if (enumName === EEmEnums.PAYMENT_MODE) {
			source = enumsPaymentMode?.map((paymentModeItem) => ({
				value: String(paymentModeItem.value),
				displayName: paymentModeItem.displayName,
			}));
		} else if (enumName === EEmEnums.IOSS_SALE_OVER_THRESHOLD_OPTION) {
			source = enumsIossSaleOverThresholdOption?.map((iossSaleOverThresholdOptionItem) => ({
				value: String(iossSaleOverThresholdOptionItem.value),
				displayName: iossSaleOverThresholdOptionItem.displayName,
			}));
		} else if (enumName === EEmEnums.IOSS_DUTY_MODES) {
			source = enumsIossDutyMode?.map((iossDutyModesItem: any) => ({
				value: String(iossDutyModesItem.value),
				displayName: iossDutyModesItem.displayName,
			}));
		} else if (enumName === EEmEnums.SUPPORTED_CURRENCY) {
			source = enumsSupportedCurrency?.map((supportedCurrencyItem) => ({
				value: String(supportedCurrencyItem.value),
				displayName: supportedCurrencyItem.displayName,
			}));
		} else if (enumName === EEmEnums.CURRENCY_CODE) {
			source = enumsCurrencyCode?.map((currencyCodeItem) => ({
				value: String(currencyCodeItem.value),
				displayName: currencyCodeItem.displayName,
			}));
		} else if (enumName === EEmEnums.EM_REPORTING_FEE_ACTUAL) {
			source = enumsReportingFeeActual?.map((reportingFeeActualItem) => ({
				value: String(reportingFeeActualItem.value),
				displayName: reportingFeeActualItem.displayName,
			}));
		} else if (enumName === EEmEnums.MANUAL_FEE_CLASS) {
			source = enumsManualFeeClass?.map((manualFeeClassItem) => ({
				value: String(manualFeeClassItem.value),
				displayName: manualFeeClassItem.displayName,
			}));
		} else if (enumName === EEmEnums.MONTHLY_FEES) {
			source = enumsMonthlyFees?.map((monthlyFeeItem) => ({
				value: String(monthlyFeeItem.value),
				displayName: monthlyFeeItem.displayName,
			}));
		} else if (enumName === EEmEnums.EMAIL_TYPE) {
			source = enumsEmailTypes?.map((emailTypeItem) => ({
				value: String(emailTypeItem.handle),
				displayName: emailTypeItem.description,
			}));
		} else if (enumName === EEmEnums.SMS_TYPE) {
			source = enumsSmsTypes?.map((smsTypeItem) => ({
				value: String(smsTypeItem.handle),
				displayName: smsTypeItem.description,
			}));
		}
		// console.debug('enumName', enumName);
		// console.debug('source', source);

		if (!source || !Array.isArray(source)) return null;
		const found = source.find((i: any) => String(i.value) === valueStr);
		return found?.displayName ?? null;
	};

	// Helper function to wrap content with tooltip if tooltip prop is provided
	const wrapWithTooltip = (
		content: React.ReactNode,
		fallbackTooltip?: string,
		keyProp?: string,
	): React.ReactNode => {
		if (!content) return content;

		// Helper to strip 'title' prop from the top-level child to avoid MUI warning
		const stripNativeTitle = (element: React.ReactElement): React.ReactElement => {
			const { title, ...restProps } = (element.props ?? {}) as Record<string, unknown>;
			return title !== undefined ? React.cloneElement(element, { ...restProps }) : element;
		};

		// Detect if element is already a MUI Tooltip to avoid nested tooltips
		const isTooltipElement = (element: React.ReactElement): boolean => {
			const elementType: any = element.type;
			return (
				elementType === Tooltip ||
				elementType === EasTooltip ||
				elementType?.muiName === 'Tooltip' ||
				elementType?.displayName === 'Tooltip'
			);
		};

		// If tooltip prop is present, always wrap elements with Tooltip
		if (tooltip && React.isValidElement(content)) {
			if (isTooltipElement(content)) return content;
			return (
				<EasTooltip key={keyProp} title={tooltip ?? ''} arrow>
					{stripNativeTitle(content as React.ReactElement)}
				</EasTooltip>
			);
		}

		// If no tooltip prop but fallback provided and content is an element, use fallback
		if (!tooltip && fallbackTooltip && React.isValidElement(content)) {
			if (isTooltipElement(content)) return content;
			return (
				<EasTooltip key={keyProp} title={fallbackTooltip} arrow>
					{stripNativeTitle(content as React.ReactElement)}
				</EasTooltip>
			);
		}

		// If content is not a ReactElement or no tooltip/fallback, return as is
		return content;
	};

	const fontSize = useMemo(() => {
		return size === 'xs'
			? '0.6rem'
			: size === 's'
				? '0.7rem'
				: size === 'm'
					? '0.8rem'
					: size === 'l'
						? '0.9rem'
						: size === 'xl'
							? '1.2rem'
							: size === 'xxl'
								? '1.6rem'
								: '1.2rem';
	}, [size]);
	const chipHeight = useMemo(() => {
		return size === 'xs'
			? '1rem'
			: size === 's'
				? '1.1rem'
				: size === 'm'
					? '1.4rem'
					: size === 'l'
						? '1.6rem'
						: '1.8rem';
	}, [size]);
	const renderCountryValue = () =>
		chip ? (
			<Chip
				label={
					<CountryWithFlag
						countryCode={value as string}
						size={size}
						showLabel={
							type === ETableStructureType.COUNTRY || type === ETableStructureType.COUNTRY_EU
						}
						tooltip={false}
					/>
				}
				sx={{
					backgroundColor: COLORS.BACKGROUND_SECONDARY,
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
				}}
			/>
		) : (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'start',
					width: '100%',
				}}
			>
				<CountryWithFlag
					countryCode={value as string}
					size={size}
					showLabel={
						type === ETableStructureType.COUNTRY || type === ETableStructureType.COUNTRY_EU
					}
					tooltip={
						type === ETableStructureType.COUNTRY_CODE ||
						type === ETableStructureType.COUNTRY_CODE_EU
					}
				/>
			</Box>
		);

	const renderDateValue = () => {
		if (!value) return null;

		return (
			<Chip
				label={DateTime.fromISO(String(value)).toFormat('dd.MM.yyyy')}
				sx={{
					// backgroundColor: 'primary.light',
					backgroundColor: '#ffffff00',
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						padding: 0,
						fontSize,
						// color: 'primary.main',
						color: COLORS.font_status,
						fontWeight: 'bold',
					},
				}}
			/>
		);
	};

	const renderDateTimeValue = () => {
		if (!value) return null;

		return (
			<Chip
				label={DateTime.fromISO(String(value)).toFormat('dd.MM.yyyy, HH:mm')}
				sx={{
					// backgroundColor: 'primary.light',
					backgroundColor: '#ffffff00',
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						padding: '0px 8px',
						fontSize,
						// color: 'primary.main',
						color: COLORS.font_status,
						fontWeight: 'bold',
					},
				}}
			/>
		);
	};

	const renderDateMonthValue = () => {
		if (!value) return null;

		return (
			<Chip
				label={DateTime.fromISO(String(value)).toFormat('MM.yyyy')}
				sx={{
					// backgroundColor: 'primary.light',
					backgroundColor: '#ffffff00',
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						padding: '0px 8px',
						fontSize,
						color: COLORS.font_status,
						fontWeight: 'bold',
					},
				}}
			/>
		);
	};

	const renderDatePeriodValue = () => {
		if (!value) return null;

		const dates: string[] = String(value).split(' - ');
		const formatted = dates
			.map((date) => DateTime.fromISO(date).toFormat('dd.MM.yyyy'))
			.join(' - ');

		return (
			<Chip
				label={formatted}
				sx={{
					// backgroundColor: 'primary.light',
					backgroundColor: '#ffffff00',
					color: COLORS.font_status,
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						padding: '0px 0px',
						fontSize,
						color: COLORS.font_status,
						fontWeight: 'bold',
					},
				}}
			/>
		);
	};

	const renderDateRangeValue = () => {
		if (!value) return null;

		// Parse PostgreSQL range format, e.g.:
		// ("2025-07-15 11:38:07.536412+00","2026-02-01 00:00:00+00")
		// ["2025-04-01 00:00:00+00","2026-02-01 00:00:00+00")
		const raw = String(value).trim();
		const inner = raw.replace(/^[[(]/, '').replace(/[\])]$/, '');
		const bounds = inner.split(',').map((part) => part.trim().replace(/^"|"$/g, ''));

		const parseBound = (bound: string): string => {
			if (!bound) return '';
			// Normalize SQL timestamp ("2025-07-15 11:38:07+00") to ISO before parsing
			const isoCandidate = bound.replace(' ', 'T');
			const parsed = DateTime.fromISO(isoCandidate, { setZone: true });
			const dateTime = parsed.isValid ? parsed : DateTime.fromSQL(bound, { setZone: true });
			return dateTime.isValid ? dateTime.toFormat('dd.MM.yyyy') : bound;
		};

		const formatted = bounds
			.map(parseBound)
			.filter((part) => part !== '')
			.join(' - ');

		if (!formatted) return null;

		return (
			<Chip
				label={formatted}
				sx={{
					backgroundColor: '#ffffff00',
					color: COLORS.font_status,
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						padding: '0px 0px',
						fontSize,
						color: COLORS.font_status,
						fontWeight: 'bold',
					},
				}}
			/>
		);
	};

	const renderAmountValue = () => {
		const amount = value === '' ? 0 : Number(value ?? 0);
		const currency =
			tableStructureItem?.currency && item != null ? tableStructureItem.currency(item) : 'EUR';
		const sxTableCell = tableStructureItem?.sxTableCell;
		const boldItem = tableStructureItem?.sxTableCell?.fontWeight ?? bold;
		const colorItem = COLORS.font_light; //tableStructureItem?.sxTableCell?.color ?? 'black';

		return (
			<AmountAndCurrency
				size={Number(fontSize.replace('rem', ''))}
				amount={amount}
				currency={currency}
				bold={boldItem ? 'bold' : 'normal'}
				color={colorItem}
				// color="primary"
				noFixedPoint={currency === '%' || currency === 'kg' ? true : false}
			/>
		);
	};

	const renderUserValue = () => {
		if (!value) return null;

		return <UserAvatar user={value as TUser} size="xxs" tooltip />;
	};

	const renderUsersValue = () => {
		if (!value) return null;

		return (
			<Box sx={{ display: 'flex', flexDirection: 'row', gap: '2px' }}>
				{value.map((user: TUser, idx: number) => (
					<UserAvatar key={String((user as any)?.id ?? idx)} user={user} size="xxs" tooltip />
				))}
			</Box>
		);
	};

	const renderStatusValue = (statusLabel: string, statusColor: string) => {
		// const statusLabel =
		// 	filter.filter?.find((filterItem: any[]) => filterItem[0] === status)?.[1] ?? status;

		// const statusColor =
		// 	filter.colors?.find((color: any[]) => color[0] === status)?.[1] ?? 'primary.light';

		// Intentionally derive text color if needed in the future
		if (!statusLabel) {
			return null;
		}

		return (
			<Chip
				label={statusLabel}
				sx={{
					backgroundColor: statusColor,
					color:
						// statusColor === 'primary.light'
						// 	? 'primary.main'
						// 	: statusColor === 'warning.light'
						// 		? 'warning.dark'
						// 		: statusColor === 'success.light'
						// 			? 'success.dark'
						// 			: statusColor === 'error.light'
						// 				? 'error.dark'
						// 				: statusColor === 'info.light'
						// 					? 'info.dark'
						// 					:
						COLORS.font_status,
					fontSize,
					borderRadius: '12px',
					height: chipHeight,
					'& .MuiChip-label': {
						// opacity: color === COLORS.font_main ? 0.5 : 1,
						// opacity: 1,
						padding: '0px 8px',
						fontSize,
						fontWeight: '550',
					},
				}}
			/>
		);
	};

	// Removed unused renderStatusNoBgValue

	const renderLinkValue = () => (
		<Typography
			component="span"
			sx={{
				color: COLORS.font_main,
				fontSize,
				textDecoration: 'none',
				fontWeight: 'bold',
				cursor: 'pointer',
			}}
		>
			{value !== null ? (value as string) : ''}
		</Typography>
	);

	const renderEmailValue = () => {
		if (!value) return null;
		return (
			<Link
				href={`mailto:${value}`}
				sx={{ fontSize }}
				onClick={(event) => {
					event.stopPropagation();
				}}
			>
				{value}
			</Link>
		);
	};

	const renderCommentsValue = () => {
		// Ensure value is an array of comments
		if (!Array.isArray(value)) {
			// value is not an array; nothing to render
			return null;
		}

		const comments = value as { comment: string; author: TUser }[];

		if (!comments || comments.length === 0) {
			return (
				<EasTooltip title="Add Comment">
					<IconButton
						color="primary"
						size="small"
						onClick={(event) =>
							handleActionStopPropagation?.(event, item?.id, TypeOfDialog.COMMENT_DIALOG)
						}
					>
						<Comment />
					</IconButton>
				</EasTooltip>
			);
		}

		const commentsContent = (
			<EasTooltip
				title={
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							justifyContent: 'start',
							alignItems: 'start',
						}}
					>
						{comments?.map((comment) => (
							<Box
								key={`${comment.author?.fullName ?? 'unknown'}-${comment.comment ?? ''}`}
								sx={{
									lineHeight: '1.2',
									textAlign: 'start',
								}}
							>
								<b>{comment.author?.fullName || 'Unknown'}:&nbsp;</b>
								<i>{comment.comment || ''}</i>
							</Box>
						))}
					</Box>
				}
				arrow
			>
				<Typography
					component="button"
					sx={{
						fontSize,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						backgroundColor: 'transparent',
						border: 'none',
						cursor: 'pointer',
						width: '100%',
						maxWidth: '200px',
						textAlign: 'start',
						color: COLORS.font_main,
					}}
					onClick={(e) => handleActionStopPropagation?.(e, item?.id, TypeOfDialog.COMMENT_DIALOG)}
				>
					<b>{comments[0]?.author?.fullName || 'Unknown'}:&nbsp;</b>
					<i>{comments[0]?.comment || ''}</i>
				</Typography>
			</EasTooltip>
		);

		return wrapWithTooltip(commentsContent, 'Comments');
	};

	const renderIconValue = () => {
		if (!tableStructureItem?.icons) return null;

		const matchedIcon = tableStructureItem.icons.find((icon: any) => icon.value === value);
		if (!matchedIcon) return null;

		return wrapWithTooltip(
			React.createElement(ICON_BUTTONS[matchedIcon.icon as EIconButtons], {
				color: matchedIcon.color ?? 'primary',
				fontSize: 'small',
				sx: { fontSize: '1.1rem', p: 0, m: 0, width: '1.1rem', verticalAlign: 'middle' },
			}),
			tableStructureItem.name ?? (value as string),
		);
	};

	const renderBooleanValue = () => {
		return value === true ? (
			<Check
				color="primary"
				fontSize="small"
				sx={{ fontSize: '1.1rem', p: 0, m: 0, width: '1.1rem', verticalAlign: 'middle' }}
			/>
		) : (
			<HorizontalRule
				color="primary"
				fontSize="small"
				sx={{ fontSize: '1.1rem', p: 0, m: 0, width: '1.1rem', verticalAlign: 'middle' }}
			/>
		);
		// 	<Chip
		// 		label="Yes"
		// 		sx={{
		// 			backgroundColor: COLORS.PRIMARY,
		// 			color: COLORS.SECONDARY,
		// 			fontSize,
		// 			borderRadius: '12px',
		// 			height: chipHeight,
		// 			fontWeight: 'bold',
		// 			width:
		// 				size === 'xs'
		// 					? '42px'
		// 					: size === 's'
		// 						? '42px'
		// 						: size === 'm'
		// 							? '44px'
		// 							: size === 'l'
		// 								? '50px'
		// 								: size === 'xl'
		// 									? '50px'
		// 									: '50px',
		// 			p: 0,
		// 		}}
		// 	/>
		// ) : value === false ? (
		// 	<Chip
		// 		label="No"
		// 		sx={{
		// 			backgroundColor: COLORS.SECONDARY,
		// 			color: COLORS.PRIMARY,
		// 			fontSize,
		// 			borderRadius: '12px',
		// 			height: chipHeight,
		// 			fontWeight: 'bold',
		// 			width: '42px',
		// 			p: 0,
		// 		}}
		// 	/>
		// ) : null;
	};

	const renderButtonValue = () => (
		<Button
			color="primary"
			// variant={selectedRow === index ? 'contained' : 'outlined'}
			// size="small"
			variant="contained"
			sx={{
				borderRadius: '12px',
				textTransform: 'none',
				height: '24px',
				padding: '4px 6px!important',
				margin: '0px',
				border: '0.5px solid #016193!important',
				fontSize,
				color: 'white!important',
			}}
			onClick={(event) =>
				handleActionStopPropagation?.(event, item?.id, TypeOfDialog.FILE_REPORT_DIALOG)
			}
		>
			{tableStructureItem?.selector as string}
		</Button>
	);

	const renderButtonsValue = () => {
		const buttons = tableStructureItem?.buttonFilter
			? tableStructureItem.buttonFilter(item)
			: tableStructureItem?.buttons;

		if (!buttons) return null;

		return (
			<Box
				sx={{
					display: 'inline-flex',
					flexDirection: 'row',
					gap: '8px',
				}}
			>
				{buttons.map((button: TTableStructureButton) => {
					const compositeKey = `${button.name}-${button.label ?? ''}-${button.color ?? ''}`;
					if (button.icon) {
						return wrapWithTooltip(
							React.createElement(ICON_BUTTONS[button.icon], {
								onClick: (event) =>
									handleActionStopPropagation?.(event, item?.id, button.name as TypeOfDialog),
								sx: {
									p: 0,
									m: 0,
									verticalAlign: 'middle',
									color: button.color ?? 'primary.main',
									cursor: 'pointer',
								},
							}),
							button.name,
							compositeKey,
						);
					}
					return (
						// <EasTooltip key={compositeKey} title={button.label ?? button.name} arrow>
						<Button
							key={compositeKey}
							color="primary"
							variant="contained"
							size="small"
							sx={{
								borderRadius: '12px',
								textTransform: 'none',
								height: '1.4rem',
								padding: '4px 6px!important',
								margin: '0px',
								fontSize,
								color: 'white!important',
								minWidth: '20px',
								backgroundColor: button.color ?? 'primary',
							}}
							onClick={(event) =>
								handleActionStopPropagation?.(event, item?.id, button.name as TypeOfDialog)
							}
						>
							{button.name}
						</Button>
						// </EasTooltip>
					);
				})}
			</Box>
		);
	};

	const renderTextValue = () => {
		const textContent = (
			<Typography
				sx={{
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
					maxWidth: '100px',
					fontSize,
					color: COLORS.font_main,
				}}
			>
				{value as string}
			</Typography>
		);

		return wrapWithTooltip(textContent, value as string);
	};

	const renderCopyableTextValue = () => {
		if (!value) return null;
		return (
			<EasTooltip title="Copy to clipboard">
				<Box
					sx={{
						display: 'inline-flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: 0.2,
						cursor: 'pointer',
						// justifyContent: 'flex-end',
					}}
					onClick={(event) => {
						event?.stopPropagation();
						navigator.clipboard
							.writeText(value as string)
							.then(() => {
								setIsCopied(true);
								setTimeout(() => {
									setIsCopied(false);
								}, 2000);
							})
							.catch(() => {
								toast.error('Copy failed');
							});
					}}
				>
					<Typography
						sx={{
							fontSize,
							color: COLORS.PRIMARY,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							fontWeight: bold ? 'bold' : 'normal',
						}}
					>
						{value as string}
					</Typography>
					<Box sx={{ position: 'relative' }}>
						<ContentCopy
							sx={{
								fontSize: '0.75rem',
								color: isCopied ? 'success.main' : 'primary.main',
								cursor: 'pointer',
								opacity: isCopied ? 0.25 : 1,
							}}
						/>
						{isCopied && (
							<CircularProgress
								size={12}
								color="primary"
								sx={{ position: 'absolute', right: 0, top: 0, zIndex: 1000 }}
							/>
						)}
					</Box>
				</Box>
			</EasTooltip>
		);
	};

	const renderMultilineTextValue = () => (
		<Typography
			sx={{
				fontSize,
				color: COLORS.font_main,
				whiteSpace: 'pre-wrap',
				wordBreak: 'break-word',
				verticalAlign: 'start',
			}}
		>
			{value as string}
		</Typography>
	);

	const renderArrayIdValue = () => {
		const mapped = resolveEnumDisplayName(tableStructureItem?.enumName, value);
		if (mapped !== null) {
			return (
				<Typography
					component="span"
					sx={{
						fontSize,
						color: COLORS.font_main,
						fontWeight: bold ? 'bold' : 'normal',
					}}
				>
					{mapped}
				</Typography>
			);
		}
		return null;
	};

	const renderArrayOfObjectsValue = () => {
		if (!value || !Array.isArray(value)) return null;
		return (
			<Box sx={{ display: 'flex', flexDirection: 'row', gap: '2px' }}>
				{value.map((item: any) => (
					<Chip
						key={item.key}
						sx={{
							backgroundColor: COLORS.BACKGROUND_SECONDARY,
							fontSize,
							color: COLORS.font_main,
							fontWeight: bold ? 'bold' : 'normal',
						}}
						size="small"
						label={`${item.key}: ${item.value ?? 0}`}
					/>
				))}
			</Box>
		);
	};

	const renderStringValue = (stringValue: string = value as string) => {
		if (value === null) return <div />;
		return (
			<Typography
				component="span"
				sx={{ fontSize, color: COLORS.font_main, fontWeight: bold ? 'bold' : 'normal' }}
			>
				{stringValue}
			</Typography>
		);
	};

	const renderDefaultValue = () => {
		if (value === null) return <div />;
		const stringValue =
			typeof value === 'string' || typeof value === 'number'
				? (String?.(value) ?? '')
				: (String?.(value ?? '') ?? '');
		// console.debug('stringValue', stringValue);

		return (
			<Typography
				component="span"
				sx={{
					fontSize,
					color: COLORS.font_main,
					fontWeight: bold ? 'bold' : 'normal',
				}}
			>
				{stringValue}
			</Typography>
		);
	};

	const renderStatusArrayItem = (status: string) => (
		<React.Fragment key={String(status)}>
			{renderStatusValue(
				tableStructureItem?.filter?.find((filterItem: any[]) => filterItem[0] === status)?.[1] ??
					status,
				tableStructureItem?.colors?.find((color: any[]) => color[0] === status)?.[1] ??
					COLORS.BACKGROUND_SECONDARY,
			)}
		</React.Fragment>
	);

	const renderStatusArrayValue = () => {
		if (!value || !Array.isArray(value) || value.length === 0) return null;

		const visibleStatuses = value.slice(0, STATUS_ARRAY_VISIBLE_LIMIT);
		const hiddenCount = value.length - visibleStatuses.length;

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'start',
					width: '100%',
					gap: '2px',
				}}
			>
				{visibleStatuses.map(renderStatusArrayItem)}
				{hiddenCount > 0 && (
					<>
						<Button
							size="small"
							variant="text"
							onClick={(e) => {
								e.stopPropagation();
								setStatusArrayAnchor(e.currentTarget);
							}}
							sx={{
								minWidth: 'auto',
								padding: '0 4px',
								fontSize,
								textTransform: 'none',
								whiteSpace: 'nowrap',
							}}
						>
							Show more (+{hiddenCount})
						</Button>
						<Popover
							open={Boolean(statusArrayAnchor)}
							anchorEl={statusArrayAnchor}
							onClose={(e: React.SyntheticEvent) => {
								e?.stopPropagation?.();
								setStatusArrayAnchor(null);
							}}
							onClick={(e) => e.stopPropagation()}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
							transformOrigin={{ vertical: 'top', horizontal: 'left' }}
						>
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: '4px',
									padding: '8px',
									maxWidth: '400px',
								}}
							>
								{value.map(renderStatusArrayItem)}
							</Box>
						</Popover>
					</>
				)}
			</Box>
		);
	};

	const renderStatusArrayVerticalValue = () => {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					gap: '4px',
				}}
			>
				{value &&
					Array.isArray(value) &&
					value.length > 0 &&
					value.map((status: string) => (
						<React.Fragment key={String(status)}>
							{renderStatusValue(
								tableStructureItem?.filter?.find(
									(filterItem: any[]) => filterItem[0] === status,
								)?.[1] ?? status,
								tableStructureItem?.colors?.find((color: any[]) => color[0] === status)?.[1] ??
									COLORS.BACKGROUND_SECONDARY,
							)}
						</React.Fragment>
					))}
			</Box>
		);
	};

	const renderCountryCodeArrayValue = () => {
		if (!value || !Array.isArray(value)) return null;

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'start',
					width: '100%',
					gap: '6px',
				}}
			>
				{value.map((countryCode: string) => (
					<CountryWithFlag
						key={countryCode}
						countryCode={countryCode}
						size="xs"
						showLabel={false}
					/>
				))}
			</Box>
		);
	};

	const renderCountryNumberValue = () => {
		return (
			<CountryWithFlag
				countryCode={registeredCountry?.isoAlpha2 ?? ''}
				size="l"
				color="primary"
				bold
				tooltip={false}
			/>
		);
	};
	const renderCountryNameValue = () => {
		return (
			<CountryWithFlag
				countryCode={getCountryByname?.isoAlpha2 ?? ''}
				size={size}
				color="primary"
				bold
				tooltip={false}
			/>
		);
	};

	const renderCheckboxValue = () => {
		return (
			<Checkbox
				checked={value}
				size="small"
				color="primary"
				inputProps={{ 'aria-label': 'Checkbox demo' }}
			/>
		);
	};

	const renderEmValue = () => {
		if (!value) return null;
		return (
			<EasTooltip title="View in EM Management">
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
					<OpenInNew
						sx={{
							fontSize:
								size === 'xs'
									? '0.5rem'
									: size === 's'
										? '0.6rem'
										: size === 'm'
											? '0.7rem'
											: size === 'l'
												? '0.8rem'
												: size === 'xl'
													? '0.9rem'
													: size === 'xxl'
														? '1rem'
														: '0.7rem',
							color,
						}}
					/>

					<Link
						href={`/em-management/${String(value.id)}`}
						target="_blank"
						rel="noopener noreferrer"
						sx={{
							textDecoration: 'none',
							fontSize,
							color,
							fontWeight: 'bold',
							cursor: 'pointer',
						}}
						onClick={(event) => {
							event.stopPropagation();
							// navigate(`/em-management/${String(value.id)}`);
						}}
					>
						{value.name}
					</Link>
				</Box>
			</EasTooltip>
		);
	};

	const renderInvoiceNumberValue = () => {
		if (!value) return null;
		return (
			<EasTooltip title="View in Invoice Management">
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
					<OpenInNew
						sx={{
							fontSize: '0.7rem',
							color: COLORS.font_main,
						}}
					/>
					<Link
						href={`/invoice-management/${String(value)}`}
						target="_blank"
						rel="noopener noreferrer"
						sx={{
							textDecoration: 'none',
							fontSize,
							color: COLORS.font_main,
							fontWeight: 'bold',
							cursor: 'pointer',
						}}
						onClick={(event) => {
							event.stopPropagation();
							// navigate(`/em-management/${String(value.id)}`);
						}}
					>
						{value}
					</Link>
				</Box>
			</EasTooltip>
		);
	};

	const renderOrderIdValue = () => {
		if (!value) return null;
		return (
			<EasTooltip title="View in Order Management">
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
					<OpenInNew
						sx={{
							fontSize: '0.7rem',
							color: color ?? COLORS.font_main,
						}}
					/>
					<Link
						href={`/order-management/${String(value)}`}
						target="_blank"
						rel="noopener noreferrer"
						sx={{
							textDecoration: 'none',
							fontSize,
							color: color ?? COLORS.font_main,
							fontWeight: 'bold',
							cursor: 'pointer',
						}}
						onClick={(event) => {
							event.stopPropagation();
							// navigate(`/em-management/${String(value.id)}`);
						}}
					>
						{value}
					</Link>
				</Box>
			</EasTooltip>
		);
	};

	const renderIossNumberValue = () => {
		if (!value) return null;
		return (
			<EasTooltip title="View in VAT Reimbursement">
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
					<OpenInNew
						sx={{
							fontSize: '0.7rem',
							color: COLORS.font_main,
						}}
					/>
					<Link
						href={`/vat-reimbursement?page=1&search=${String(value)}`}
						target="_blank"
						rel="noopener noreferrer"
						sx={{
							fontSize,
							color: COLORS.font_main,
							fontWeight: 'bold',
							cursor: 'pointer',
						}}
						onClick={(event) => {
							event.stopPropagation();
						}}
					>
						{value}
					</Link>
				</Box>
			</EasTooltip>
		);
	};

	// Wrap all render functions with tooltip if tooltip prop is provided
	const renderWithTooltip = (renderFunction: () => React.ReactNode): React.ReactNode => {
		const content = renderFunction();
		if (!content) return null;
		return wrapWithTooltip(content);
	};

	const renderContent = () => {
		switch (type) {
			case ETableStructureType.EM:
				return renderWithTooltip(renderEmValue);
			case ETableStructureType.INVOICE_NUMBER:
				return renderWithTooltip(renderInvoiceNumberValue);
			case ETableStructureType.ORDER_ID:
				return renderWithTooltip(renderOrderIdValue);
			case ETableStructureType.IOSS_NUMBER:
				return renderWithTooltip(renderIossNumberValue);
			case ETableStructureType.COUNTRY:
			case ETableStructureType.COUNTRY_CODE:
				return renderWithTooltip(renderCountryValue);

			case ETableStructureType.COUNTRY_CODE_EU:
			case ETableStructureType.COUNTRY_EU:
				return renderWithTooltip(renderCountryValue);

			case ETableStructureType.COUNTRY_CODE_ARRAY:
				return renderWithTooltip(renderCountryCodeArrayValue);

			case ETableStructureType.COUNTRY_NUMBER:
				return renderWithTooltip(renderCountryNumberValue);

			case ETableStructureType.COUNTRY_NAME:
				return renderWithTooltip(renderCountryNameValue);

			case ETableStructureType.DATE:
				return renderWithTooltip(renderDateValue);

			case ETableStructureType.DATE_TIME:
				return renderWithTooltip(renderDateTimeValue);

			case ETableStructureType.DATE_MONTH:
				return renderWithTooltip(renderDateMonthValue);

			case ETableStructureType.DATE_PERIOD:
				return renderWithTooltip(renderDatePeriodValue);

			case ETableStructureType.DATE_RANGE:
				return renderWithTooltip(renderDateRangeValue);

			case ETableStructureType.AMOUNT:
				return renderWithTooltip(renderAmountValue);

			case ETableStructureType.USER:
				return renderWithTooltip(renderUserValue);

			case ETableStructureType.USERS:
				return renderWithTooltip(renderUsersValue);

			case ETableStructureType.STATUS:
				if (
					value === null &&
					!tableStructureItem?.filter?.find((filterItem: any[]) => filterItem[0] === null)
				)
					return null;

				return renderWithTooltip(() =>
					renderStatusValue(
						tableStructureItem?.filter?.find(
							(filterItem: any[]) => String(filterItem[0]) === String(value),
						)?.[1] ?? value,
						tableStructureItem?.colors?.find(
							(color: any[]) => String(color[0]) === String(value),
						)?.[1] ?? COLORS.BACKGROUND_SECONDARY,
					),
				);

			case ETableStructureType.STATUS_OBJECT:
				if (!value) return null;
				return renderWithTooltip(() =>
					renderStatusValue(
						value.statusName,
						tableStructureItem?.colors?.find(
							(color: any[]) => String(color[0]) === String(value.statusName),
						)?.[1] ?? COLORS.BACKGROUND_SECONDARY,
					),
				);

			case ETableStructureType.STATUS_NO_BG:
				if (
					value === null &&
					!tableStructureItem?.filter?.find((filterItem: any[]) => filterItem[0] === null)
				)
					return null;

				return renderWithTooltip(
					() => (
						<Typography
							component="span"
							sx={{ fontSize, color: COLORS.font_main, fontWeight: 'bold' }}
						>
							{tableStructureItem?.filter?.find(
								(filterItem: any[]) => String(filterItem[0]) === String(value),
							)?.[1] ?? value}
						</Typography>
					),

					// renderStringValue(
					// 	tableStructureItem?.filter?.find(
					// 		(filterItem: any[]) => String(filterItem[0]) === String(value),
					// 	)?.[1] ?? value,
					// ),
				);

			case ETableStructureType.STATUS_ARRAY:
				return renderWithTooltip(renderStatusArrayValue);

			case ETableStructureType.EMAIL:
				return renderWithTooltip(renderEmailValue);

			case ETableStructureType.STATUS_ARRAY_VERTICAL:
				return renderWithTooltip(renderStatusArrayVerticalValue);

			case ETableStructureType.LINK:
				return renderWithTooltip(renderLinkValue);

			case ETableStructureType.COMMENTS:
				return renderCommentsValue();

			case ETableStructureType.ICON:
				return renderIconValue();

			case ETableStructureType.BOOLEAN:
				return renderWithTooltip(renderBooleanValue);

			case ETableStructureType.BUTTON:
				return renderWithTooltip(renderButtonValue);

			case ETableStructureType.BUTTONS:
				return renderWithTooltip(renderButtonsValue);

			case ETableStructureType.TEXT:
				return renderTextValue();

			case ETableStructureType.MULTILINE_TEXT:
				return renderWithTooltip(renderMultilineTextValue);

			case ETableStructureType.COPYABLE_TEXT:
				return renderWithTooltip(renderCopyableTextValue);

			case ETableStructureType.ARRAY_ID:
				return renderWithTooltip(renderArrayIdValue);
			case ETableStructureType.ARRAY_OF_OBJECTS:
				return renderWithTooltip(renderArrayOfObjectsValue);
			case ETableStructureType.STRING:
				return renderWithTooltip(renderStringValue);

			case ETableStructureType.CHECKBOX:
				return renderWithTooltip(renderCheckboxValue);

			default:
				return renderWithTooltip(renderDefaultValue);
		}
	};

	const content = renderContent();

	// Always return a JSX element or null
	if (!content) {
		return null;
	}

	// If content is already a JSX element, return it
	if (React.isValidElement(content)) {
		return content;
	}

	// If content is a primitive value, wrap it in a span
	return <span>{content}</span>;
};
